import { useId } from 'react'
import CtaLink, { type CtaLinkProps } from './cta-link'
import Link from '@/components/link-component'
import HubspotCTA, { type HubspotCTAProps } from './hubspot-cta'
import type { Entry } from 'contentful'
import type { TypeComponentCtaLinkSkeleton, TypeComponentHubspotCtaSkeleton } from '@/types/ctf'

type CtaLink = {
  type?: 'ctaLink'
} & CtaLinkProps

type CtaLinkEntry = Entry<TypeComponentCtaLinkSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'>
type CtaLinkWithType = Partial<Omit<CtaLinkEntry['fields'], 'link' | 'style'>> & {
  type: 'componentCtaLink'
  link?: Partial<CtaLinkEntry['fields']['link']> | null
} & CtaLinkProps

type HubspotCtaEntry = Entry<TypeComponentHubspotCtaSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'>
type HubspotCtaWithType = Omit<HubspotCtaEntry['fields'], 'style'> & {
  type: 'componentHubspotCta'
} & HubspotCTAProps

export type DynamicActionProps = (CtaLink | CtaLinkWithType | HubspotCtaWithType) & {
  id?: string
  label?: string
  nonCtaLink?: boolean
}

/** Renders the correct CTA component based on the type */
export default function DynamicAction(fields: DynamicActionProps) {
  const allProps = fields.fields ?? fields

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { internalName, ...props } = allProps

  const contentfulId = fields.id ? fields.id : props.link?.sys?.id
  const uid = useId() + contentfulId

  switch (props.type) {
    case 'componentHubspotCta':
      const { type: hubspotTypename, ...hubspotCtaProps } = props
      return <HubspotCTA {...hubspotCtaProps}>{props.label}</HubspotCTA>
    case 'componentCtaLink':
      const { link, type: contentfulCtaLinkTypename, ...contentfulCtaLinkProps } = props

      return (
        props?.link?.fields?.url && (
          <CtaLink {...contentfulCtaLinkProps} id={uid} to={link?.fields?.url}>
            {props.label}
          </CtaLink>
        )
      )
    case 'ctaLink':
    default:
      const { type: ctaLinkTypename, ...ctaLinkProps } = props

      //avoids using the CtaLink component and complex css inheritance
      return fields.nonCtaLink ? (
        <Link
          aria-label={`go to ${props.link?.fields?.url}`}
          to={props.link?.fields?.url}
          className={`${fields.className} ${fields.plausibleEventName}`}
          id={uid}
          data-testid="cta-link"
          tabIndex={fields.tabindex}
          onClick={fields.onClick ? (e: React.MouseEvent) => fields.onClick(e) : undefined}
        >
          {props.label || props.children}
        </Link>
      ) : (
        <CtaLink
          className={fields.className}
          to={ctaLinkProps.link?.fields?.url}
          {...ctaLinkProps}
          id={uid}
          tabIndex={fields.tabindex}
        >
          {props.label || props.children}
        </CtaLink>
      )
  }
}
